/**
 * @file AdminShellLayout.tsx
 * @brief Shared admin shell — fixed header/footer with scroll-collapse.
 *
 * @details
 * Header and footer are `position: fixed` and translate off-screen on
 * scroll-down. Main padding is ALWAYS reserved for chrome height — never
 * shrunk when collapsed. Shrinking padding mid-scroll used to fight
 * `scrollTop` compensation on small screens (tall mobile footer), causing
 * jitter and chrome that stayed hidden until a refresh.
 *
 * Content sits in a centered ~1280px rail (no horizontal pad on main —
 * GridBlock and plain pages share the same width). Continuous md+ hairline
 * side guides are drawn once on the viewport (`ShellRailGuides`). Shell
 * canvas is `bg-app-page`, the phone's page colour (#F5F5F5 / dark #232221, since
 * 30-09-2026); white panels sit on it like the phone's cards. Main sits flush
 * under the fixed topbar (no extra top gap) so page grids connect to chrome.
 *
 * Related: shared/hooks/useScrollCollapse.ts, AppFooter.tsx (taller on
 * mobile), FRONTEND MeetingSetup padding lesson (07-07-2026).
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — unpadded main rail (consistent page width)
 * @updated 30-09-2026 — header's `data-shell-pinned` row stays sticky on collapse
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { CommandPalette } from "@/features/command-palette/CommandPalette";
import { useCommandPaletteHotkey } from "@/features/command-palette/useCommandPaletteHotkey";
import type { PaletteScope } from "@/features/command-palette/commandPaletteTypes";
import { cn } from "@/shared/theme/cn";
import { useScrollCollapse } from "@/shared/hooks/useScrollCollapse";
import { MainScrollProvider } from "@/shared/scroll/MainScrollContext";
import { AppFooter } from "./AppFooter";
import { ShellRailGuides } from "./ShellRailGuides";
import { shellRailMaxClass } from "./shell-rail";
import { PlanNoticeDialog } from "@/shared/billing/PlanNoticeDialog";
import { Topbar } from "./Topbar";
import type { NavShellConfig } from "./nav-types";

const CONTENT_GAP_TOP = 0;
const CONTENT_GAP_BOTTOM = 16;

interface AdminShellLayoutProps {
  nav: NavShellConfig;
  /** Cmd+K palette (28-07-2026); on for normal users too since 21-10-2026. */
  enableCommandPalette?: boolean;
  /** Whose palette; the /app shell passes "user". */
  paletteScope?: PaletteScope;
}

export function AdminShellLayout({
  nav,
  enableCommandPalette = true,
  paletteScope,
}: AdminShellLayoutProps) {
  const scrollRef = useRef<HTMLElement>(null);
  const headerWrapRef = useRef<HTMLDivElement>(null);
  const footerWrapRef = useRef<HTMLDivElement>(null);
  const programmaticScrollRef = useRef(false);
  const [commandOpen, setCommandOpen] = useState(false);

  // Cmd+K / Ctrl+K — global jump/search palette (17-07-2026)
  const toggleCommandPalette = useCallback(() => {
    if (!enableCommandPalette) return;
    setCommandOpen((prev) => !prev);
  }, [enableCommandPalette]);
  useCommandPaletteHotkey(enableCommandPalette ? toggleCommandPalette : null);

  // Pause collapse tracking while an anchor jump is animating, so chrome
  // can't re-collapse and yank the scroll position mid-smooth-scroll.
  const isCollapsePaused = useCallback(() => programmaticScrollRef.current, []);
  const setProgrammaticScroll = useCallback((active: boolean) => {
    programmaticScrollRef.current = active;
  }, []);

  const { collapsed, expand } = useScrollCollapse(scrollRef, isCollapsePaused);

  const [headerHeight, setHeaderHeight] = useState(56);
  const [footerHeight, setFooterHeight] = useState(48);
  // Height of the header's pinned row (desktop quick-nav). It stays on screen
  // when chrome collapses; only the rows above it slide away. 0 when hidden.
  const [pinnedHeight, setPinnedHeight] = useState(0);

  useEffect(() => {
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const pinnedEl = headerWrapRef.current?.querySelector<HTMLElement>(
      "[data-shell-pinned]",
    );
    const measure = () => {
      setHeaderHeight(headerWrapRef.current?.offsetHeight ?? 56);
      setFooterHeight(footerWrapRef.current?.offsetHeight ?? 48);
      setPinnedHeight(pinnedEl?.offsetHeight ?? 0);
    };

    measure();

    const ro = new ResizeObserver(measure);
    if (headerWrapRef.current) ro.observe(headerWrapRef.current);
    if (footerWrapRef.current) ro.observe(footerWrapRef.current);
    if (pinnedEl) ro.observe(pinnedEl);

    return () => ro.disconnect();
  }, []);

  // ----------------------------------------------------------------------
  // Stable chrome padding
  // Always reserve header/footer space. Collapse only translates chrome
  // off-screen — no padding reflow, no scrollTop compensation loop.
  // Related: useScrollCollapse.ts, AppFooter.tsx (mobile tabs add height).
  // ----------------------------------------------------------------------
  // What actually slides off-screen on collapse: the header minus its pinned row.
  const collapsibleHeaderHeight = Math.max(0, headerHeight - pinnedHeight);

  // A page with its own bottom dock (Home) asks for no gap, so the dock's
  // fade meets the footer instead of leaving a bare band (30-09-2026).
  const [flushBottom, setFlushBottom] = useState(false);
  const mainPadding = {
    paddingTop: headerHeight + CONTENT_GAP_TOP,
    paddingBottom: footerHeight + (flushBottom ? 0 : CONTENT_GAP_BOTTOM),
  };

  // Page colour = the phone's page (#F5F5F5 / dark #232221), DES 30-09-2026.
  // White panels and cards sit on it, as the phone's cards do.
  return (
    <div className="app-page-bg relative flex h-screen flex-col overflow-hidden">
      <ShellRailGuides />

      <div
        ref={headerWrapRef}
        className="fixed inset-x-0 top-0 z-40 transition-transform duration-300 ease-out motion-reduce:transition-none"
        style={
          collapsed
            ? { transform: `translateY(-${collapsibleHeaderHeight}px)` }
            : undefined
        }
      >
        <Topbar
          nav={nav}
          onOpenCommandPalette={
            enableCommandPalette ? () => setCommandOpen(true) : undefined
          }
        />
      </div>

      {/* A plan change, once (plans-visible brief C, 21-10-2026). */}
      <PlanNoticeDialog />

      {enableCommandPalette ? (
        <CommandPalette
          nav={nav}
          scope={paletteScope}
          open={commandOpen}
          onOpenChange={setCommandOpen}
        />
      ) : null}

      <MainScrollProvider
        expandChrome={expand}
        setProgrammaticScroll={setProgrammaticScroll}
        chromeCollapsed={collapsed}
        headerHeight={collapsibleHeaderHeight}
        setFlushBottom={setFlushBottom}
      >
        <main
          ref={scrollRef}
          data-scroll-root
          className="overflow-anchor-none flex-1 overflow-y-auto"
          style={{ ...mainPadding, overflowAnchor: "none" }}
        >
          <div className={cn(shellRailMaxClass, "min-h-full")}>
            <Outlet />
          </div>
        </main>
      </MainScrollProvider>

      <div
        ref={footerWrapRef}
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ease-out motion-reduce:transition-none",
          collapsed && "pointer-events-none translate-y-full",
        )}
      >
        <AppFooter />
      </div>
    </div>
  );
}
